import { useTheme } from '@shared/providers/theme/ThemeContext';
import React, { useCallback, useRef, useState, useEffect } from 'react';

interface CollapsibleCardProps {
  title?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  extraHeaderContent?: React.ReactNode;
  headerBackgroundColor?: string;
  collapsible?: boolean;
  defaultExpanded?: boolean;
}

export const CollapsibleCardContext = React.createContext(false);

const CollapsibleCard: React.FC<CollapsibleCardProps> = ({
  title,
  children,
  className,
  extraHeaderContent,
  headerBackgroundColor,
  collapsible = false,
  defaultExpanded = true,
}) => {
  const { colors } = useTheme();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (!collapsible || !contentRef.current) return;
    setContentHeight(contentRef.current.scrollHeight);
  }, [collapsible, children]);

  const toggleExpanded = useCallback(() => {
    if (collapsible) setExpanded(prev => !prev);
  }, [collapsible]);

  const hasHeader = title !== undefined && title !== null && title !== '';

  return (
    <CollapsibleCardContext.Provider value>
      <div className={`mb-3 shadow-md flex min-w-0 flex-col ${className || ''}`}>
        {hasHeader && (
          <div
            className={`flex items-center justify-between px-4 py-2 bg-white shadow-sm${expanded ? ' rounded-t' : ' rounded'}${collapsible ? ' cursor-pointer select-none' : ''}`}
            style={{
              backgroundColor: headerBackgroundColor ?? colors.primary,
              color: colors.sectionHeaderText,
            }}
            onClick={toggleExpanded}
          >
            <span className="flex-1 text-base">{title}</span>
            <div className="flex items-center gap-2">
              {extraHeaderContent}
              {collapsible && (
                <span className="flex items-center gap-1 text-sm">
                  <i
                    className={`fa fa-angle-${expanded ? 'up' : 'down'}`}
                    aria-hidden="true"
                    style={{ transition: 'transform 0.3s ease' }}
                  />
                  {expanded ? ' 閉じる' : ' 開く'}
                </span>
              )}
            </div>
          </div>
        )}
        {collapsible ? (
          <div
            ref={contentRef}
            style={{
              maxHeight: expanded ? (contentHeight ?? 'none') : 0,
              overflow: 'hidden',
              transition: 'max-height 0.3s ease',
            }}
          >
            <div
              className={`bg-white border-t border-gray-100${hasHeader ? ' rounded-b' : ' rounded'} shadow-sm`}
            >
              <div className="p-4">{children}</div>
            </div>
          </div>
        ) : (
          <div
            className={`bg-white border-t border-gray-100${hasHeader ? ' rounded-b' : ' rounded'} shadow-sm flex-1 flex min-w-0 flex-col`}
          >
            <div className="min-w-0 flex-1 p-4">{children}</div>
          </div>
        )}
      </div>
    </CollapsibleCardContext.Provider>
  );
};

export const useIsInsideCollapsibleCard = () => React.useContext(CollapsibleCardContext);

export default CollapsibleCard;
